import { FC, ReactElement } from "react";
import { Navigate, RouteObject, useLocation, useRoutes } from "react-router-dom";
import {
  // Errors
  NotFound,

  // Auth
  Login,
  ForgotPassword,
  SetNewPassword,
  ResetPasswordSuccess,
  Terms,
  Privacy,
} from "@/pages";
import { AuthLayout, LoggedinLayout } from "@/layouts";
import PublicRoute from "./public-route";
import PrivateRoute from "./private-route";
import { RolesRoutes } from "@/roles/routes";
import { roles } from "@/roles";
import { useAppSelector } from "@/store";
import Careers from "@/pages/careers";
import { COMMON } from "@/pages";
import { useGetAllTenantsQuery } from "@/services";

const Routes: FC = (): ReactElement | null => {
  const user = useAppSelector((state) => state.auth.user);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const location = useLocation();

  const getPrivateRoleRoutes = () => {
    if (user?.userRole === roles.SPOWNER) {
      return [...RolesRoutes.SPOWNER];
    } else if (user?.userRole === roles.SPPARTNER) {
      return [...RolesRoutes.SPPARTNER];
    } else if (user?.userRole === roles.SPCLIENT) {
      return [...RolesRoutes.SPCLIENT];
    } else if (user?.userRole === roles.TDCLIENT) {
      return [...RolesRoutes.TDCLIENT];
    } else if (user?.userRole === roles.TDADMIN) {
      return [...RolesRoutes.TDADMIN ,...(selectedTenant?.tenantType === "SERVICE_PROVIDER" ? [...RolesRoutes.TDADMIN_SP] : [])];
    }
    return [];
  };

  // Prevent 404 on refresh: if admin is on SP-only paths (e.g., /partners or /clients)
  // wait until tenant selection is hydrated (or tenants finish loading) before rendering routes
  const isAdmin = user?.userRole === roles.TDADMIN;
  const pathname = location.pathname || "";
  const isSPSection = pathname.startsWith("/partners") || pathname.startsWith("/clients");
  // Prefetch tenants early for admins to hydrate selection (unconditional hook with skip)
  const {
    isError: isTenantPrefetchError,
    isSuccess: isTenantPrefetchSuccess,
  } = useGetAllTenantsQuery(
    { searchKeyword: "", pageNo: 1, pageSize: 200 },
    { refetchOnMountOrArgChange: true, skip: !isAdmin }
  );
  // If admin on SP section and selection not ready yet, wait to avoid 404
  if (isAdmin && isSPSection && !selectedTenant) {
    if (isTenantPrefetchError || isTenantPrefetchSuccess) {
      return <Navigate to="/dashboard/analytics" replace />;
    }

    return null;
  }

  const routes: RouteObject[] = [
    {
      path: "/",
      element: <PrivateRoute navLink="/" component={LoggedinLayout} />,
      children: getPrivateRoleRoutes(),
    },
    {
      path: "/login",
      element: <PublicRoute component={AuthLayout} />,
      children: [
        {
          path: "",
          element: <Login />,
        },
      ],
    },
    {
      path: "/forgot-password",
      element: <PublicRoute component={AuthLayout} />,
      children: [
        {
          path: "",
          element: <ForgotPassword />,
        },
      ],
    },
    {
      path: "/set-new-password",
      element: <PublicRoute component={AuthLayout} />,
      children: [
        {
          path: "",
          element: <SetNewPassword />,
        },
      ],
    },
    {
      path: "/reset-password-success",
      element: <PublicRoute component={AuthLayout} />,
      children: [
        {
          path: "",
          element: <ResetPasswordSuccess />,
        },
      ],
    },
    {
      path: "/careers",
      element: <Careers />,
    },
    {
      path: "/supplier-portal/:token",
      element: <COMMON.FirstSaleSupplierPortal />,
    },
    {
      path: "/terms",
      element: <Terms />,
    },
    {
      path: "/privacy",
      element: <Privacy />,
    },
    { path: "404", element: <NotFound /> },
    {
      path: "*",
      element: <Navigate to="/404" />,
    },
  ];

  return useRoutes([...routes]);
};

export default Routes;
